import "@refinedev/antd/dist/reset.css";
import "./global.css";
import "utils/date";

import { queryClient } from "@desk/utils/queryClient";
import { css } from "@emotion/css";
import { Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import routerBindings, { UnsavedChangesNotifier } from "@refinedev/react-router-v6";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ConfigProvider } from "antd";
import { toSafeInteger } from "lodash-es";
import type { FC, PropsWithChildren } from "react";
import { Fragment, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertProvider, DocumentTitleHandler } from "ui";
import { ErrorBoundary } from "ui/error";
import { useWebworker, WebworkerProvider } from "utils/contexts";
import { useMessage, useNetworkTimeout } from "utils/hooks";

import { authProvider } from "@/authProvider";
import { useGlobalStore } from "@/store";

import { accessControlProvider } from "./accessControlProvider";
import { APP_RESOURCE } from "./app.constant";
import dataProvider from "./dataProvider";
import { en, ja, zh } from "./locale";
import { notificationProvider } from "./notificationProvider";
import { formConfig, theme } from "./themeConfig";

const TIMEOUT = import.meta.env.VITE_NETWORK_TIMEOUT;

export const AppWrapper: FC<PropsWithChildren> = ({ children }) => {
  const { t, i18n } = useTranslation(["common", "property", "device", "option", "button", "form"]);
  const { contextHolder, message } = useMessage();
  const { propertyId } = useGlobalStore();
  const { initWorker, cleanupWorker } = useWebworker();

  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };
  const antdLocale = useMemo(() => {
    switch (i18n.language) {
      case "zh":
        return zh;
      case "jp":
        return ja;
      default:
        return en;
    }
  }, [i18n.language]);

  useEffect(() => {
    const worker = new Worker(new URL("./batch-generate-pdf.js", import.meta.url), {
      type: "module",
    });
    initWorker(worker);
    return () => {
      cleanupWorker();
    };
  }, []);

  return (
    <ConfigProvider form={formConfig} locale={antdLocale} theme={theme}>
      <RefineKbarProvider>
        <Refine
          accessControlProvider={accessControlProvider}
          authProvider={authProvider}
          dataProvider={dataProvider}
          i18nProvider={i18nProvider}
          key={propertyId}
          notificationProvider={notificationProvider(message)}
          options={{
            syncWithLocation: false,
            warnWhenUnsavedChanges: true,
            projectId: "A69idI-86AY7i-77NzCo",
            reactQuery: { clientConfig: queryClient },
          }}
          resources={APP_RESOURCE}
          routerProvider={routerBindings}
        >
          <div
            className={css`
              .ant-pagination-item,
              .ant-pagination-prev,
              .ant-pagination-next {
                border: 1px solid #d9d9d9 !important;
              }
              .ant-pagination-item-active {
                border-color: #00afcc !important;
              }
            `}
          >
            <AlertProvider>
              {contextHolder}
              <Inside>{children}</Inside>
              <RefineKbar />
              <UnsavedChangesNotifier />
              <DocumentTitleHandler title="Pabbit Desk" />
            </AlertProvider>
          </div>
        </Refine>
      </RefineKbarProvider>
    </ConfigProvider>
  );
};
const Inside: FC<PropsWithChildren> = ({ children }) => {
  useNetworkTimeout(toSafeInteger(TIMEOUT) || undefined);
  return <Fragment>{children}</Fragment>;
};
const Wrapper: FC<PropsWithChildren> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <WebworkerProvider>
          <AppWrapper>{children}</AppWrapper>
        </WebworkerProvider>
        <ReactQueryDevtools />
      </ErrorBoundary>
    </QueryClientProvider>
  );
};
export default Wrapper;
